import { useQueryClient } from "@tanstack/vue-query";
import SystemPracticeStaffService, {
  type TSystemPracticeStaff,
  type TSystemPracticeStaffListFilters,
} from "@/api/models/staff/systemPracticeStaff";
import { type MaybeRefOrGetter } from "vue";
import { useSnackStore } from "@/stores/snackStore";
import type { PaginatedResponse } from "@/api/shared/filters";
import type { InfiniteData } from "@tanstack/vue-query";
import { useAuthStore } from "@/stores/authStore";
import { useRouter } from "vue-router";
import {
  useGenericPaginatedQuery,
  useGenericQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

const SystemPracticeStaffQueryKeys = createQueryKeys(
  SystemPracticeStaffService.queryKey,
  (key) => ({
    paginated: key<TSystemPracticeStaffListFilters>("paginated"),
    detail: key("detail"),
  })
);

export const useSystemPracticeStaff = (
  params: MaybeRefOrGetter<TItemQueryComposableParams<string>>,
  options: TItemQueryBehaviorOptions = {}
) =>
  useGenericQuery<TSystemPracticeStaff>({
    queryKey: SystemPracticeStaffQueryKeys.detail(),
    params,
    fetcher: (id) => SystemPracticeStaffService.get({ id }),
    permission: $permSubjects.PRACTICE__PRACTICESTAFF,
    ...options,
  });

export const useSystemPracticeStaffsPaginatedFiltered = (
  params: TOwnedFilterParams<TSystemPracticeStaffListFilters> = {}
) => {
  return useGenericPaginatedQuery<TSystemPracticeStaff, Partial<TSystemPracticeStaffListFilters>>({
    params,
    queryKey: SystemPracticeStaffQueryKeys.paginated(),
    fetcher: (fetchParams) =>
      SystemPracticeStaffService.getPaginatedFiltered(
        {},
        fetchParams.filters,
        fetchParams.pageParam
      ),
    permission: $permSubjects.PRACTICE__PRACTICESTAFF,
  });
};

export const useUpdateSystemPracticeStaffGroups = () => {
  const qc = useQueryClient();

  return useGenericMutation<TSystemPracticeStaff, { staffId: string; groupsIds: number[] }>({
    mutationFn: ({ staffId, groupsIds }) =>
      SystemPracticeStaffService.update({ id: staffId }, { group_ids: groupsIds }),
    feature: "update-system-practice-staff-groups",
    options: {
      onSuccess: (updated) => {
        const replaceInPage = (
          page: PaginatedResponse<TSystemPracticeStaff>
        ): PaginatedResponse<TSystemPracticeStaff> => ({
          ...page,
          results: page.results.map((result) => (result.id === updated.id ? updated : result)),
        });

        qc.setQueriesData<InfiniteData<PaginatedResponse<TSystemPracticeStaff>>>(
          { queryKey: SystemPracticeStaffQueryKeys.paginated() },
          (current) => {
            if (!current?.pages) return current;
            // eslint-disable-next-line unicorn/no-array-callback-reference
            return { ...current, pages: current.pages.map(replaceInPage) };
          }
        );
      },
      onError: () => {
        qc.invalidateQueries({ queryKey: SystemPracticeStaffQueryKeys.paginated() });
      },
    },
  });
};

export const useImpersonateSystemPracticeStaff = () => {
  const snack = useSnackStore();
  const qc = useQueryClient();
  const authStore = useAuthStore();
  const router = useRouter();

  return useGenericMutation<TSystemPracticeStaff, string>({
    mutationFn: (staffId) => SystemPracticeStaffService.impersonate({ id: staffId }, undefined),
    feature: "impersonate-system-practice-staff",
    options: {
      onSuccess: async () => {
        await authStore.setMe(true);
        void qc.resetQueries();
        snack.showSuccess("Now impersonating user");
        await router.push("/");
      },
    },
  });
};

export const useStopImpersonatingSystemPracticeStaff = () => {
  const snack = useSnackStore();
  const qc = useQueryClient();
  const authStore = useAuthStore();
  const router = useRouter();

  return useGenericMutation<TSystemPracticeStaff, void>({
    mutationFn: () => SystemPracticeStaffService.stopImpersonating({}, undefined),
    options: {
      onSuccess: async () => {
        await authStore.setMe(true);
        void qc.resetQueries();
        snack.showSuccess("Now logged in as yourself");
        // Force reload the page to clear the impersonation state
        router.go(0);
      },
    },
  });
};
